import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { Modal } from '@/components/UI/Modal';
import { Button } from '@/components/UI/Button';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge } from '@/components/UI/Badge';
import { Alert } from '@/components/UI/Alert';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { getErrorDetail } from '@/utils/apiError';
import {
  useListPlatformPlansQuery,
  useCreatePlatformPlanMutation,
  useUpdatePlatformPlanMutation,
  useDeletePlatformPlanMutation,
  PlatformPlan,
  CreatePlanRequest,
} from '@/redux/internal-services/platformAdminApi';

const EMPTY_FORM: CreatePlanRequest = {
  name: '',
  description: '',
  price_monthly: null,
  price_annual: null,
  user_limit: null,
  features: [],
  trial_eligible: false,
  trial_duration_days: null,
};

const PlanCard: React.FC<{
  plan: PlatformPlan;
  onEdit: (plan: PlatformPlan) => void;
  onDelete: (plan: PlatformPlan) => void;
  onToggle: (plan: PlatformPlan) => void;
}> = ({ plan, onEdit, onDelete, onToggle }) => (
  <div className={cn('card-surface p-6 transition', plan.is_active === false && 'opacity-60')}>
    <div className="flex items-start justify-between mb-3">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-ui-text">{plan.name}</h3>
          {plan.is_active === false && (
            <Badge variant="neutral">Inactive</Badge>
          )}
          {plan.trial_eligible && (
            <Badge variant="accent">Trial</Badge>
          )}
        </div>
        <p className="text-sm text-ui-text2 mt-0.5">{plan.description}</p>
      </div>
      <div className="flex gap-2 ml-4">
        <Button
          variant={plan.is_active === false ? 'secondary' : 'outline'}
          size="xs"
          className={plan.is_active === false ? 'text-success-text' : undefined}
          onClick={() => onToggle(plan)}
        >
          {plan.is_active === false ? 'Enable' : 'Disable'}
        </Button>
        <Button variant="soft" size="xs" onClick={() => onEdit(plan)}>
          Edit
        </Button>
        <Button variant="destructive-ghost" size="xs" onClick={() => onDelete(plan)}>
          Delete
        </Button>
      </div>
    </div>

    <div className="flex flex-wrap gap-4 mb-4">
      <div>
        <p className="text-xs text-ui-text3">Monthly</p>
        <p className="text-h3 text-ui-text">
          {plan.price_monthly != null ? `$${plan.price_monthly}` : 'Custom'}
        </p>
      </div>
      <div>
        <p className="text-xs text-ui-text3">Annual</p>
        <p className="text-h3 text-ui-text">
          {plan.price_annual != null ? `$${plan.price_annual}` : 'Custom'}
        </p>
      </div>
      {plan.user_limit != null && (
        <div>
          <p className="text-xs text-ui-text3">User Limit</p>
          <p className="text-h3 text-ui-text">{plan.user_limit}</p>
        </div>
      )}
      {plan.trial_duration_days != null && plan.trial_eligible && (
        <div>
          <p className="text-xs text-ui-text3">Trial Days</p>
          <p className="text-h3 text-ui-text">{plan.trial_duration_days}</p>
        </div>
      )}
    </div>

    {(plan.features ?? []).length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {plan.features!.map((f) => (
          <Badge key={f} variant="neutral">{f}</Badge>
        ))}
      </div>
    )}
  </div>
);

type ModalMode = 'create' | 'edit';

const PlanFormModal: React.FC<{
  mode: ModalMode;
  initial: CreatePlanRequest & { plan_id?: string };
  onClose: () => void;
  onSave: (data: CreatePlanRequest & { plan_id?: string }) => void;
  saving: boolean;
  error: string;
}> = ({ mode, initial, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState(initial);
  const [featureInput, setFeatureInput] = useState('');

  const update = (key: keyof typeof form, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const addFeature = () => {
    const f = featureInput.trim();
    if (f && !(form.features ?? []).includes(f)) {
      update('features', [...(form.features ?? []), f]);
    }
    setFeatureInput('');
  };

  const removeFeature = (f: string) =>
    update('features', (form.features ?? []).filter((x) => x !== f));

  return (
    <Modal
      title={mode === 'create' ? 'Create Plan' : 'Edit Plan'}
      onClose={onClose}
      onSave={() => onSave(form)}
      saving={saving}
      saveLabel={mode === 'create' ? 'Create Plan' : 'Save Changes'}
      closeOnBackdrop={false}
    >
      <div>
        <label className="block text-sm font-medium text-ui-text mb-1">Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          className={fieldClass()}
          placeholder="e.g. Professional"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-ui-text mb-1">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          rows={2}
          className={cn(fieldClass({ shape: 'box' }), 'resize-none')}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-ui-text mb-1">Monthly Price ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.price_monthly ?? ''}
            onChange={(e) => update('price_monthly', e.target.value === '' ? null : Number(e.target.value))}
            className={fieldClass()}
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-ui-text mb-1">Annual Price ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.price_annual ?? ''}
            onChange={(e) => update('price_annual', e.target.value === '' ? null : Number(e.target.value))}
            className={fieldClass()}
            placeholder="0.00"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-ui-text mb-1">User Limit (leave blank for unlimited)</label>
        <input
          type="number"
          min="1"
          value={form.user_limit ?? ''}
          onChange={(e) => update('user_limit', e.target.value === '' ? null : Number(e.target.value))}
          className={fieldClass()}
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.trial_eligible ?? false}
          onChange={(e) => update('trial_eligible', e.target.checked)}
          className="h-4 w-4 accent-accent"
        />
        <span className="text-sm text-ui-text">Trial eligible</span>
      </label>

      {form.trial_eligible && (
        <div>
          <label className="block text-sm font-medium text-ui-text mb-1">Trial Duration (days)</label>
          <input
            type="number"
            min="1"
            value={form.trial_duration_days ?? ''}
            onChange={(e) => update('trial_duration_days', e.target.value === '' ? null : Number(e.target.value))}
            className={fieldClass()}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-ui-text mb-1">Features</label>
        <div className="mb-2 flex items-center gap-2">
          <input
            type="text"
            value={featureInput}
            onChange={(e) => setFeatureInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
            className={cn(fieldClass(), 'min-w-0 flex-1')}
            placeholder="Add a feature..."
          />
          <Button type="button" onClick={addFeature}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(form.features ?? []).map((f) => (
            <Badge key={f} variant="accent" className="gap-1">
              {f}
              <button type="button" onClick={() => removeFeature(f)} className="text-accent-text/60 hover:text-accent-text">×</button>
            </Badge>
          ))}
        </div>
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
    </Modal>
  );
};

const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useListPlatformPlansQuery();
  const [createPlan, { isLoading: creating }] = useCreatePlatformPlanMutation();
  const [updatePlan, { isLoading: updating }] = useUpdatePlatformPlanMutation();
  const [deletePlan] = useDeletePlatformPlanMutation();

  const plans: PlatformPlan[] = data?.data ?? [];

  const [modal, setModal] = useState<null | { mode: ModalMode; plan: CreatePlanRequest & { plan_id?: string } }>(null);
  const [modalError, setModalError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<PlatformPlan | null>(null);
  const [globalMsg, setGlobalMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const openCreate = () => { setModal({ mode: 'create', plan: { ...EMPTY_FORM } }); setModalError(''); };
  const openEdit = (plan: PlatformPlan) => {
    setModal({
      mode: 'edit',
      plan: {
        plan_id: plan.plan_id,
        name: plan.name,
        description: plan.description,
        price_monthly: plan.price_monthly,
        price_annual: plan.price_annual,
        user_limit: plan.user_limit ?? null,
        features: plan.features ?? [],
        trial_eligible: plan.trial_eligible ?? false,
        trial_duration_days: plan.trial_duration_days ?? null,
      },
    });
    setModalError('');
  };

  const handleSave = async (formData: CreatePlanRequest & { plan_id?: string }) => {
    setModalError('');
    if (!formData.name.trim()) { setModalError('Plan name is required.'); return; }
    try {
      if (modal?.mode === 'create') {
        await createPlan(formData).unwrap();
        setGlobalMsg({ type: 'success', text: 'Plan created.' });
      } else if (formData.plan_id) {
        const { plan_id, ...rest } = formData;
        await updatePlan({ planId: plan_id!, data: rest }).unwrap();
        setGlobalMsg({ type: 'success', text: 'Plan updated.' });
      }
      setModal(null);
      refetch();
    } catch (err: unknown) {
      setModalError(getErrorDetail(err) ?? 'Failed to save plan.');
    }
  };

  const handleToggle = async (plan: PlatformPlan) => {
    try {
      await updatePlan({ planId: plan.plan_id, data: { is_active: plan.is_active === false ? true : false } }).unwrap();
      refetch();
    } catch { /* silently fail */ }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deletePlan(deleteConfirm.plan_id).unwrap();
      setGlobalMsg({ type: 'success', text: 'Plan deleted.' });
      setDeleteConfirm(null);
      refetch();
    } catch (err: unknown) {
      setGlobalMsg({ type: 'error', text: getErrorDetail(err) ?? 'Failed to delete plan.' });
      setDeleteConfirm(null);
    }
  };

  return (
    <PageShell
      maxWidth="max-w-5xl"
      header={
        <PageHeader
          title="Subscription Plans"
          subtitle="Manage available plans and pricing for tenants."
          actions={
            <Button onClick={openCreate}>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Plan
            </Button>
          }
        />
      }
    >

      {globalMsg && (
        <Alert tone={globalMsg.type === 'error' ? 'danger' : 'success'} className="mb-6">
          {globalMsg.text}
        </Alert>
      )}

      {isLoading ? (
        <LoadingState label="Loading plans..." />
      ) : plans.length === 0 ? (
        <EmptyState
          variant="inline"
          title={t('platform.noPlansYet')}
          action={
            <Button variant="link" size="xs" className="text-accent-text" onClick={openCreate}>
              {t('platform.createFirstPlan')}
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan_id}
              plan={plan}
              onEdit={openEdit}
              onDelete={(p) => setDeleteConfirm(p)}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      {/* Plan form modal */}
      {modal && (
        <PlanFormModal
          mode={modal.mode}
          initial={modal.plan}
          onClose={() => setModal(null)}
          onSave={handleSave}
          saving={creating || updating}
          error={modalError}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <Modal
          title="Delete Plan"
          onClose={() => setDeleteConfirm(null)}
          onSave={handleDelete}
          saveLabel="Delete"
          danger
          maxWidthClassName="max-w-sm"
        >
          <p className="text-sm text-ui-text2">
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
          </p>
        </Modal>
      )}
    </PageShell>
  );
};

export default PlansPage;
